/**
 * BuilderBasics.
 *
 * Section 1 of the Recurring Builder drawer. Collects the template
 * name and the recurring mode (Scheduled / Reminder / Unscheduled).
 *
 * Status is NOT exposed here. Pause / Resume / End happen through
 * the row actions menu on the Recurring list page, not through the
 * builder, which keeps the Save flow about the schedule shape
 * rather than its lifecycle.
 *
 * The draft and update handler come from useRecurringBuilder; this
 * component is purely presentational.
 */

import type { CSSProperties } from "react";
import { BuilderSection } from "./RecurringBuilder";
import type {
  BuilderDraft,
  BuilderValidationErrors,
} from "./builderTypes";
import type { RecurringMode } from "../recurringTypes";

export interface BuilderBasicsProps {
  draft: BuilderDraft;
  errors: BuilderValidationErrors;
  updateDraft: (partial: Partial<BuilderDraft>) => void;
}

interface ModeOption {
  value: RecurringMode;
  title: string;
  description: string;
}

/**
 * Mode option labels and descriptions. Kept here (not in mockData)
 * because the exact wording and ordering is a builder UX concern,
 * not shared data.
 */
const MODE_OPTIONS: ModeOption[] = [
  {
    value: "scheduled",
    title: "Scheduled",
    description: "Fires automatically on the schedule",
  },
  {
    value: "reminder",
    title: "Reminder",
    description: "You get a reminder, you post it manually",
  },
  {
    value: "unscheduled",
    title: "Unscheduled",
    description: "Template only, run it on demand",
  },
];

export function BuilderBasics({
  draft,
  errors,
  updateDraft,
}: BuilderBasicsProps) {
  return (
    <BuilderSection number={1} title="Basics">
      {/* Template name */}
      <div style={fieldStyle}>
        <label style={fieldLabelStyle} htmlFor="builder-name">
          Template name
        </label>
        <input
          id="builder-name"
          type="text"
          value={draft.name}
          onChange={(e) => updateDraft({ name: e.target.value })}
          placeholder="e.g. Rent, Shopify subscription"
          style={{
            ...textInputStyle,
            ...(errors.name ? inputErrorStyle : {}),
          }}
          autoFocus={draft.mode === "create"}
        />
        {errors.name && <div style={errorTextStyle}>{errors.name}</div>}
      </div>

      {/* Mode (3 radio cards side by side) */}
      <div style={fieldStyle}>
        <div style={fieldLabelStyle}>Mode</div>
        <div style={modeGridStyle}>
          {MODE_OPTIONS.map((opt) => {
            const selected = draft.recurringMode === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => updateDraft({ recurringMode: opt.value })}
                aria-pressed={selected}
                style={{
                  ...modeCardStyle,
                  ...(selected ? modeCardSelectedStyle : {}),
                }}
              >
                <div style={modeCardTitleStyle}>{opt.title}</div>
                <div style={modeCardDescStyle}>{opt.description}</div>
              </button>
            );
          })}
        </div>
      </div>
    </BuilderSection>
  );
}

// ---------- Styles ----------

const fieldStyle: CSSProperties = {
  marginBottom: 14,
};

const fieldLabelStyle: CSSProperties = {
  display: "block",
  fontSize: 11,
  fontWeight: 600,
  color: "#3A4A52",
  marginBottom: 5,
  letterSpacing: "0.02em",
};

const textInputStyle: CSSProperties = {
  width: "100%",
  padding: "9px 12px",
  border: "1px solid #D8DEE6",
  borderRadius: 7,
  fontSize: 13,
  background: "#ffffff",
  color: "#0E1A1A",
  fontFamily: "inherit",
  outline: "none",
  boxSizing: "border-box",
};

const inputErrorStyle: CSSProperties = {
  borderColor: "#B42318",
};

const errorTextStyle: CSSProperties = {
  marginTop: 4,
  fontSize: 11.5,
  color: "#B42318",
};

const modeGridStyle: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr 1fr",
  gap: 8,
};

const modeCardStyle: CSSProperties = {
  padding: "12px 10px",
  border: "1.5px solid #D8DEE6",
  borderRadius: 8,
  cursor: "pointer",
  background: "#ffffff",
  textAlign: "left",
  fontFamily: "inherit",
  transition: "border-color 0.15s, background 0.15s",
};

const modeCardSelectedStyle: CSSProperties = {
  borderColor: "#0E1A1A",
  background: "#F3F5F5",
};

const modeCardTitleStyle: CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  color: "#0E1A1A",
  marginBottom: 3,
};

const modeCardDescStyle: CSSProperties = {
  fontSize: 10.5,
  color: "#3A4A52",
  lineHeight: 1.35,
};

export default BuilderBasics;